// ============================================================================
// useStagePreviews.ts
//
// « Monter sur le plateau », salle d'attente (2026-10-16) : avant d'accepter ou
// de refuser, le réalisateur voit la caméra de chaque spectateur en attente et
// parle à celui qu'il a sélectionné.
//
// Une liaison WebRTC directe par demande, entre le portail du spectateur et la
// régie (relais TURN au besoin) : ni microservice ni place de caméra, puisque
// rien de tout cela ne part à l'antenne. Le portail OFFRE (vidéo + micro, et un
// canal de données), la régie RÉPOND ; les deux SDP transitent par la demande
// (admin-api ↔ customer-api), faute de canal temps réel entre les deux. ICE
// complet avant l'envoi : aucun candidat à faire suivre ensuite.
//
// Le son : on n'entend que le spectateur sélectionné. La voix du réalisateur
// ne part que vers lui, et seulement tant que « Parler » est allumé ; le micro
// est rendu dès qu'il s'éteint. Le canal de données dit au portail s'il est
// regardé (sélectionné) et si on lui parle, pour qu'il l'affiche.
//
// Une offre plus récente (reconnexion du portail) remplace la liaison : le
// numéro `previewVersion` de la liste suffit à le voir.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';

import { defaultRTCConfig } from '../../../dataTypes/liveStreamManagment/utils_webrtc';
import {
  StagePendingRequest, StageRequestError, fetchStagePreviewOffer, sendStagePreviewAnswer,
} from '../../../backendServices/stageManagment/api_stageRequests';

export type StagePreviewStatus = 'none' | 'connecting' | 'connected' | 'lost';

export interface StagePreview {
  stream : MediaStream | null;
  status : StagePreviewStatus;
}

interface Link {
  version     : number;
  pc          : RTCPeerConnection;
  stream      : MediaStream;
  audio       : HTMLAudioElement;
  audioSender : RTCRtpSender | null;
  channel     : RTCDataChannel | null;
  status      : StagePreviewStatus;
  hasVideo    : boolean;
}

/** Au-delà, on répond avec les candidats déjà trouvés (relais TURN compris d'ordinaire). */
const ICE_GATHERING_MAX_MS = 4000;
/** Nouvelle tentative après un échec réseau de l'échange. */
const RETRY_MS = 5000;

const waitIceGathering = (pc: RTCPeerConnection) => new Promise<void>(resolve => {
  if (pc.iceGatheringState === 'complete') { resolve(); return; }
  let timer = 0;
  const done = () => {
    pc.removeEventListener('icegatheringstatechange', check);
    window.clearTimeout(timer);
    resolve();
  };
  const check = () => { if (pc.iceGatheringState === 'complete') done(); };
  pc.addEventListener('icegatheringstatechange', check);
  timer = window.setTimeout(done, ICE_GATHERING_MAX_MS);
});

const closeLink = (link: Link) => {
  try { link.channel?.close(); } catch { /* déjà fermé */ }
  try { link.pc.close(); } catch { /* déjà fermé */ }
  link.audio.pause();
  link.audio.srcObject = null;
};

export function useStagePreviews(
  adminId    : number | undefined,
  pending    : StagePendingRequest[],
  selectedId : number | null,
  talking    : boolean,
  onMicError : (message: string) => void,
): Record<number, StagePreview> {
  const linksRef       = useRef(new Map<number, Link>());
  /** Demandes dont l'échange est en cours, avec la version demandée. */
  const negotiatingRef = useRef(new Map<number, number>());
  const micRef         = useRef<MediaStreamTrack | null>(null);
  const selectedRef    = useRef(selectedId);
  const talkingRef     = useRef(talking);
  selectedRef.current  = selectedId;
  talkingRef.current   = talking;

  const [previews,  setPreviews]  = useState<Record<number, StagePreview>>({});
  const [retryTick, setRetryTick] = useState(0);

  const publish = useCallback(() => {
    const next: Record<number, StagePreview> = {};
    linksRef.current.forEach((link, id) => {
      next[id] = { stream: link.hasVideo ? link.stream : null, status: link.status };
    });
    setPreviews(next);
  }, []);

  /** Qui entend qui, et ce que le portail doit afficher. */
  const applyRouting = useCallback(() => {
    linksRef.current.forEach((link, id) => {
      const selected = id === selectedRef.current;
      const speaking = selected && talkingRef.current && micRef.current !== null;

      link.audio.muted = !selected;
      if (selected) void link.audio.play().catch(() => undefined);

      void link.audioSender?.replaceTrack(speaking ? micRef.current : null).catch(() => undefined);

      if (link.channel?.readyState === 'open') {
        link.channel.send(JSON.stringify({ type: 'state', watching: selected, talking: speaking }));
      }
    });
  }, []);

  const negotiate = useCallback(async (id: number, listedVersion: number) => {
    if (!adminId) return;
    negotiatingRef.current.set(id, listedVersion);

    try {
      const { offer, version } = await fetchStagePreviewOffer(adminId, id);
      if (linksRef.current.get(id)?.version === version) return;

      const previous = linksRef.current.get(id);
      if (previous) closeLink(previous);

      const pc     = new RTCPeerConnection(defaultRTCConfig());
      const stream = new MediaStream();
      const audio  = new Audio();
      audio.autoplay  = true;
      audio.srcObject = stream;
      audio.muted     = selectedRef.current !== id;

      const link: Link = { version, pc, stream, audio, audioSender: null, channel: null, status: 'connecting', hasVideo: false };
      linksRef.current.set(id, link);
      publish();

      pc.ontrack = event => {
        stream.addTrack(event.track);
        if (event.track.kind === 'video') link.hasVideo = true;
        publish();
      };
      pc.ondatachannel = event => {
        link.channel = event.channel;
        event.channel.onopen = () => applyRouting();
      };
      pc.onconnectionstatechange = () => {
        if (linksRef.current.get(id) !== link) return;
        link.status = pc.connectionState === 'connected' ? 'connected'
                    : pc.connectionState === 'failed' || pc.connectionState === 'closed' ? 'lost'
                    : 'connecting';
        publish();
      };

      await pc.setRemoteDescription({ type: 'offer', sdp: offer });

      // Le portail offre sa vidéo en envoi seul et son micro en aller-retour : la
      // voie audio de retour porte la voix du réalisateur, sans piste tant qu'il
      // ne parle pas (rien n'est émis).
      pc.getTransceivers().forEach(transceiver => {
        if (transceiver.receiver.track.kind === 'audio') {
          transceiver.direction = 'sendrecv';
          link.audioSender = transceiver.sender;
        } else {
          transceiver.direction = 'recvonly';
        }
      });

      await pc.setLocalDescription(await pc.createAnswer());
      await waitIceGathering(pc);
      if (linksRef.current.get(id) !== link) return;

      await sendStagePreviewAnswer(adminId, id, version, pc.localDescription?.sdp ?? '');
      applyRouting();
    } catch (err) {
      const link = linksRef.current.get(id);
      if (link) { closeLink(link); linksRef.current.delete(id); publish(); }

      // 404 : le spectateur n'a plus d'offre (demande close entre-temps) ;
      // 409 : il en a refait une, la liste suivante la montrera. Sinon, on réessaie.
      const status = err instanceof StageRequestError ? err.status : 0;
      if (status !== 404 && status !== 409) window.setTimeout(() => setRetryTick(t => t + 1), RETRY_MS);
    } finally {
      if (negotiatingRef.current.get(id) === listedVersion) negotiatingRef.current.delete(id);
    }
  }, [adminId, applyRouting, publish]);

  // Les demandes et leurs versions : une liaison par caméra ouverte.
  const versionsKey = pending.map(r => `${r.id}:${r.previewVersion ?? 0}`).join(',');

  useEffect(() => {
    const wanted = new Map<number, number>();
    pending.forEach(r => { if ((r.previewVersion ?? 0) > 0) wanted.set(r.id, r.previewVersion ?? 0); });

    let changed = false;
    linksRef.current.forEach((link, id) => {
      if (!wanted.has(id)) { closeLink(link); linksRef.current.delete(id); changed = true; }
    });
    if (changed) publish();

    wanted.forEach((version, id) => {
      if (linksRef.current.get(id)?.version === version) return;
      if (negotiatingRef.current.get(id) === version) return;
      void negotiate(id, version);
    });
    // `pending` est lu à travers `versionsKey` : seules les versions comptent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionsKey, retryTick, negotiate, publish]);

  // Le micro du réalisateur : ouvert seulement pendant qu'il parle.
  useEffect(() => {
    let cancelled = false;

    if (talking && selectedId !== null) {
      if (micRef.current) { applyRouting(); return; }
      navigator.mediaDevices
        .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false } })
        .then(media => {
          const track = media.getAudioTracks()[0] ?? null;
          if (cancelled || !talkingRef.current) { track?.stop(); return; }
          micRef.current = track;
          applyRouting();
        })
        .catch(() => onMicError("La régie n'a pas accès au micro : autorisez-le dans le navigateur pour parler au spectateur."));
    } else {
      micRef.current?.stop();
      micRef.current = null;
      applyRouting();
    }

    return () => { cancelled = true; };
  }, [talking, selectedId, applyRouting, onMicError]);

  // Le panneau disparaît (chaîne quittée) : tout est rendu.
  useEffect(() => () => {
    linksRef.current.forEach(closeLink);
    linksRef.current.clear();
    micRef.current?.stop();
    micRef.current = null;
  }, []);

  return previews;
}
